import React from "react";
import { useTranslate } from "@refinedev/core";
import { Tabs } from "@libertify/ui";
import { Flex } from "antd";
import type { AddonActivation, AddonDetail, QuizPreparation } from "types/";
import { formatPct } from "../utils/formatPct";
import { analyticsTheme } from "../analyticsTheme";
import { AddonPanelHeader, type AddonStat } from "./AddonPanelHeader";
import { QuizResults } from "./QuizResults";
import { QuizPreparationSection } from "./QuizPreparationSection";
import { bestPriorAddon } from "./utils/preparation";
import { hardestItem, mergeItems, quizQuestions } from "./utils/metrics";

const T_PREFIX = "media.projects.analytics.addons";
const BAD = analyticsTheme.metricError;

export type QuizPanelProps = {
  activation: AddonActivation;
  detail: AddonDetail;
  preparation: QuizPreparation | null;
  /** The preparation request failed: say so rather than "nobody finished". */
  preparationFailed?: boolean;
  projectSessions: number;
};

export const QuizPanel: React.FC<QuizPanelProps> = ({
  activation,
  detail,
  preparation,
  preparationFailed = false,
  projectSessions,
}) => {
  const t = useTranslate();
  const number = (index: number) =>
    t(`${T_PREFIX}.itemNumber.quiz`, { n: index + 1 });

  // One entry per question of the pool, in order, whatever the publication
  // (the admin preview included) or wording the answers came from.
  const positions = mergeItems(detail.items);
  const known = new Map(
    (preparation?.questions ?? []).map((q) => [q.item_index, q])
  );
  const questions = quizQuestions(positions, activation.total_items, known);
  const hardest = hardestItem(questions);
  const bestPrior = bestPriorAddon(preparation);

  const stats: AddonStat[] = [
    {
      key: "avgScore",
      label: t(`${T_PREFIX}.quiz.avgScore`),
      metric: activation.avg_quiz_score,
      type: "percentage",
      subtitle: t(
        activation.avg_quiz_score == null
          ? `${T_PREFIX}.kpi.quizScoreEmpty`
          : `${T_PREFIX}.kpi.quizScoreHint`
      ),
    },
    {
      key: "hardest",
      label: t(`${T_PREFIX}.quiz.hardest`),
      type: "percentage",
      valueColor: BAD,
      ...(hardest
        ? {
            prefix: `${number(hardest.item.item_index)} ·`,
            metric: hardest.rate,
            subtitle: t(`${T_PREFIX}.quiz.hardestHint`),
          }
        : { metric: null }),
    },
    // Hidden until both sides have enough viewers to compare.
    ...(bestPrior
      ? [
          {
            key: "bestPrior",
            label: t(`${T_PREFIX}.quiz.bestPrior`),
            type: "percentage" as const,
            prefix: t(`${T_PREFIX}.names.${bestPrior.addon}`) + " ·",
            metric: bestPrior.score,
            subtitle: t(`${T_PREFIX}.quiz.bestPriorHint`, {
              pct: formatPct(bestPrior.baseline),
            }),
          },
        ]
      : []),
  ];

  return (
    <Flex vertical gap={16}>
      <AddonPanelHeader
        addon="quiz"
        activation={activation}
        projectSessions={projectSessions}
        count={
          activation.total_items != null
            ? t(`${T_PREFIX}.quiz.poolSize`, { count: activation.total_items })
            : null
        }
        stats={stats}
      />

      <Tabs
        variant="pill"
        fullWidth
        items={[
          {
            key: "results",
            label: t(`${T_PREFIX}.quiz.resultsTab`),
            children: (
              <QuizResults
                detail={detail}
                positions={positions}
                questions={questions}
                sessionsInteracted={activation.sessions_interacted}
              />
            ),
          },
          {
            key: "impact",
            label: t(`${T_PREFIX}.prep.tab`),
            children: (
              <QuizPreparationSection
                preparation={preparation}
                preparationFailed={preparationFailed}
              />
            ),
          },
        ]}
      />
    </Flex>
  );
};
